import React, { Component } from 'react';
import { node, string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { IconCollection } from '../../components';
import css from './BookingFormErrorBoundary.module.css';

class BookingFormErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI.
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Log the error to an error reporting service
        console.error('BookingForm Error:', error, errorInfo);
        this.setState({
            error,
            errorInfo,
        });
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null, errorInfo: null });
        // Optionally reload the page or reset form state
        window.location.reload();
    };

    render() {
        const { hasError } = this.state;
        const { children, className, rootClassName } = this.props;

        if (hasError) {
            const classes = classNames(rootClassName || css.root, className);

            return (
                <div className={classes}>
                    <div className={css.errorContainer}>
                        <div className={css.iconWrapper}>
                            <IconCollection name="errorIcon" className={css.errorIcon} />
                        </div>
                        <h2 className={css.errorTitle}>
                            <FormattedMessage
                                id="BookingFormErrorBoundary.title"
                                defaultMessage="Oops! Something went wrong"
                            />
                        </h2>
                        <p className={css.errorMessage}>
                            <FormattedMessage
                                id="BookingFormErrorBoundary.message"
                                defaultMessage="We're having trouble loading the booking form. Please refresh the page to try again."
                            />
                        </p>
                        <button
                            className={css.retryButton}
                            onClick={this.handleReset}
                            type="button"
                        >
                            <FormattedMessage
                                id="BookingFormErrorBoundary.retryButton"
                                defaultMessage="Refresh Page"
                            />
                        </button>
                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <details className={css.errorDetails}>
                                <summary>Error Details (Development Only)</summary>
                                <pre className={css.errorStack}>
                                    {this.state.error.toString()}
                                    {this.state.errorInfo && this.state.errorInfo.componentStack}
                                </pre>
                            </details>
                        )}
                    </div>
                </div>
            );
        }

        return children;
    }
}

BookingFormErrorBoundary.defaultProps = {
    className: null,
    rootClassName: null,
};

BookingFormErrorBoundary.propTypes = {
    children: node.isRequired,
    className: string,
    rootClassName: string,
};

export default BookingFormErrorBoundary;

